/**
 * Matemática de cor da marca — deriva uma paleta acessível de UMA cor.
 *
 * O cadastro da operadora guarda só a cor primária. O texto sobre ela nunca pode
 * ser branco fixo: em cores claras (amarelo) o branco some. Aqui calculamos, pela
 * luminância relativa do WCAG, qual tinta de texto (clara ou escura) tem mais
 * contraste, e derivamos os tons de apoio (hover, claro, fundo suave) a partir da
 * mesma matiz — pra o app inteiro ler variáveis e nunca mais um `#fff` cravado.
 *
 * Esta é a MESMA lógica validada no laboratório de marca antes de virar código.
 */

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

export function hexToRgb(h) {
  h = String(h || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export const rgbToHex = ({ r, g, b }) =>
  "#" +
  [r, g, b]
    .map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0"))
    .join("");

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

const withL = (rgb, l) => {
  const hsl = rgbToHsl(rgb);
  return hslToRgb({ h: hsl.h, s: hsl.s, l: clamp(l, 0, 1) });
};

export function relLum({ r, g, b }) {
  const f = (v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrast(a, b) {
  const la = relLum(a), lb = relLum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// A tinta de texto que o app renderiza. Preto de viés teal (não puro) pra
// combinar com a UI; o cálculo usa a cor real renderizada, então o número de
// contraste é verdadeiro.
export const INK_DARK = { r: 0x10, g: 0x16, b: 0x13 }; // #101613
export const INK_LIGHT = { r: 255, g: 255, b: 255 };

// Piso de contraste para texto GRANDE/negrito (WCAG AA) — é o que as superfícies
// de marca carregam (botão, header, badge, nav ativa). AA para texto corrido é
// 4.5, mas a marca raramente pinta texto pequeno.
export const AA_LARGE = 3.0;
export const AA_BODY = 4.5;

/**
 * A tinta de texto sobre a marca.
 *
 * Regra (decidida com o Victor, 2026-08): PRESERVA o branco enquanto ele serve
 * para texto grande — só troca pra tinta escura quando o branco realmente falha.
 * Assim o visual da Elosaúde (teal, texto branco a 3.55:1) fica intacto, e só as
 * cores claras problemáticas (amarelo, branco ~1.8:1) passam a ter texto escuro.
 *
 * Seguro por construção: os contrastes de preto e branco sobre uma mesma cor
 * multiplicam ~21, então o melhor dos dois é sempre >= ~4.6 — nunca fica ilegível.
 */
export function pickInk(brand) {
  const cWhite = contrast(brand, INK_LIGHT);
  const cDark = contrast(brand, INK_DARK);
  if (cWhite >= AA_LARGE) return { rgb: INK_LIGHT, ratio: cWhite, isLight: true };
  return cDark >= cWhite
    ? { rgb: INK_DARK, ratio: cDark, isLight: false }
    : { rgb: INK_LIGHT, ratio: cWhite, isLight: true };
}

/** Deriva a paleta inteira de uma cor primária (aceita hex ou rgb). */
export function derive(input) {
  const brand = typeof input === "string" ? hexToRgb(input) : input;
  if (!brand) return null;
  const ink = pickInk(brand);
  const hsl = rgbToHsl(brand);
  const dark = withL(brand, hsl.l - 0.12);
  const light = withL(brand, clamp(hsl.l + 0.16, 0, 0.9));
  const soft = {
    r: brand.r * 0.12 + 255 * 0.88,
    g: brand.g * 0.12 + 255 * 0.88,
    b: brand.b * 0.12 + 255 * 0.88,
  };
  return {
    brand: rgbToHex(brand),
    ink: rgbToHex(ink.rgb),
    inkRatio: ink.ratio,
    dark: rgbToHex(dark),
    light: rgbToHex(light),
    soft: rgbToHex(soft),
  };
}

/** Menor ajuste de brilho (mesma matiz) que faz o texto passar em AA (4.5:1),
 *  ou null se a cor já passa. Alimenta o aviso do cadastro da operadora. */
export function suggestAA(input) {
  const brand = typeof input === "string" ? hexToRgb(input) : input;
  if (!brand) return null;
  const ink = pickInk(brand);
  if (ink.ratio >= 4.5) return null;
  const hsl = rgbToHsl(brand);
  const dir = ink.isLight ? -1 : 1; // texto branco -> escurece a marca; preto -> clareia
  for (let step = 1; step <= 100; step++) {
    const cand = hslToRgb({ h: hsl.h, s: hsl.s, l: clamp(hsl.l + (dir * step) / 100, 0, 1) });
    if (pickInk(cand).ratio >= 4.5) return rgbToHex(cand);
  }
  return null;
}
